import styled, { css } from 'styled-components';

import CustomIconBase from '@components/utils/CustomIcon';
import { PaletteColor, SizeUnit } from '@utils/types';
import Button from '@components/Button/next';
import ShowMoreText from '@libs/react-show-more-text';

export const BlockTitle = styled.h4`
  color: ${(p) => p.theme.palette.coral};
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 10px;
`;

export const Block = styled.section<{
  maxWidth?: SizeUnit;
  backgroundColor?: PaletteColor;
  padding?: SizeUnit;
}>`
  display: flex;
  flex-direction: column;
  margin-top: 16px;
  border-radius: 8px;
  ${BlockTitle} {
    margin-bottom: 20px;
  }
  max-width: ${(p) => p.maxWidth ?? '820px'};
  padding: ${(p) => p.padding ?? 0};
  background-color: ${(p) => p.backgroundColor};
  width: 100%;
`;

export const SectionHeader = styled.div<{
  marginTop?: SizeUnit;
  marginBottom?: SizeUnit;
  overrideDesktopMargin?: SizeUnit;
}>`
  display: flex;
  align-items: flex-end;
  margin-bottom: 10px;
  margin-top: ${({ marginTop }) => marginTop};
  @media (max-width: ${(p) => p.theme.breakPoint.PHONE}) {
    margin-bottom: ${({ marginBottom }) => marginBottom ?? '-12px'};
    align-items: center;
    & > span:first-child {
      align-self: flex-start;
    }
  }
`;

export const Section = styled.section<{
  color?: PaletteColor;
  variant?: 'payment';
  blur?: boolean;
}>`
  margin-bottom: 20px;
  background-color: ${({ theme }) => theme.palette.white};
  ${(p) =>
    p.variant === 'payment' &&
    css`
      display: flex;
      flex-direction: column;
      min-height: 91px;
      opacity: ${p.blur ? 0.3 : 1};
      transition: opacity 0.2s ease;
      ${SectionHeader} {
        margin-top: 13.5px;
      }
    `}
  color: ${({ color, theme }) => color && theme.palette[color]};
`;

export const SectionTitle = styled.h3<{ variant?: 'payment' }>`
  color: ${(p) => p.theme.palette.D500};
  letter-spacing: 1px;
  font-weight: 700;
  vertical-align: bottom;
  text-align: center;
  margin-left: 3px;
  &[data-role='mail-info-text'] {
    font-size: 20px;
    @media (max-width: ${(p) => p.theme.breakPoint.PHONE}) {
      font-size: 16px;
    }
  }
  ${(p) =>
    p.variant === 'payment' &&
    css`
      font-size: 20px;
      margin: 0 0 0 10px;
      line-height: 1;
      @media (max-width: ${(p) => p.theme.breakPoint.PHONE}) {
        font-size: 18px;
      }
    `}
`;

export const CustomIcon = styled(CustomIconBase)`
  display: flex;
  align-items: flex-end;
`;

export const TextAreaBox = styled.textarea<{
  height?: SizeUnit;
  hasError?: boolean;
}>`
  width: 100%;
  border-radius: 8px;
  border: solid 1px
    ${({ theme, hasError }) =>
      hasError ? theme.palette.coral : theme.palette.inputBorderColor};
  background-color: ${(p) => p.theme.palette.lynxWhite};
  resize: none;
  padding-top: 24px;
  padding-left: 15px;
  padding-right: 15px;
  font-size: 16px;
  &:focus {
    outline: none;
  }
  height: ${(p) => p.height ?? '100%'};
  box-sizing: border-box;
  &:disabled {
    opacity: 0.5;
  }
`;

export const BillingCheckBoxLabel = styled.span<{ useSmallerFont: boolean }>`
  padding: 0 8px;
  color: ${(p) => p.theme.palette.brownishGrey};
  font-size: ${(p) => (p.useSmallerFont ? 13 : 14)}px;
  line-height: 1.57px;
  & > span {
    color: ${(p) => p.theme.palette.coral};
  }
`;

export const ImportantText = styled.span`
  color: ${(p) => p.theme.palette.coral};
  cursor: pointer;
  margin-right: 3px;
  text-decoration: underline;
`;
export const Span = styled.span`
  cursor: pointer;
`;

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;
export const ButtonWrapper = styled.div`
  display: flex;
  background-color: ${(p) => p.theme.palette.white};
  margin-bottom: 20px;
  border-radius: 8px;
`;

export const Paragraph = styled.p`
  white-space: pre-line;
  margin: 0;
  padding-left: 30px;
`;
export const ParagraphCheckBox = styled.p`
  cursor: pointer;
`;
export const ParagraphControl = styled.p`
  padding-left: 30px;
  margin-top: -20px;
`;
export const ContractText = styled.p`
  margin: 0;
`;
export const ContractTextDistanceSelling = styled.p`
  white-space: pre-line;
  margin: 0;
  max-height: 36px;
`;

export const ShowMoreLink = styled.span`
  text-decoration: underline;
  font-weight: 700;
`;
export const CustomShowMoreText = styled(ShowMoreText)``;

export const LoyaltyButtonWr = styled.div`
  position: absolute;
  right: 0;
  margin-right: 20px;
  margin-bottom: 15px;

  @media ${({ theme }) => `(max-width: ${theme.breakPoint.SMALL})`} {
    margin-right: 10px;
    margin-bottom: 0px;
  }
`;
export const LoyaltyButtonWrapper = styled.div`
  position: absolute;
  right: 0;
  margin-right: 20px;
  margin-bottom: 15px;

  @media ${({ theme }) => `(max-width: ${theme.breakPoint.SMALL})`} {
    margin-right: 10px;
    margin-bottom: 0px;
  }
`;

export const StyledButton = styled(Button)`
  && {
    font-size: 12px;
    border-radius: 50px;
    border-color: ${({ theme }) => theme.palette.eclipse};
    color: ${({ theme }) => theme.palette.NL0};
    background-color: ${({ theme }) => theme.palette.eclipse};

    width: 174px;
    height: 39px;

    display: flex;
    align-items: center;

    &:hover {
      background-color: ${({ theme }) => theme.palette.eclipse};
      border-color: ${({ theme }) => theme.palette.eclipse};
      box-shadow: 0 10px 30px 0 ${({ theme }) => theme.palette.gray34};
    }

    &:active {
      background-color: ${({ theme }) => theme.palette.eclipse};
      border-color: ${({ theme }) => theme.palette.eclipse};
    }

    &:focus {
      background-color: ${({ theme }) => theme.palette.eclipse};
      border-color: ${({ theme }) => theme.palette.eclipse};
    }

    @media ${({ theme }) => `(max-width: ${theme.breakPoint.SMALL})`} {
      width: 170px;
      height: 30px;
    }
  }
`;

export const ButtonIconWrapper = styled.div`
  margin-left: auto;
`;
export const DismissButton = styled(Button)`
  && {
    height: 40px;
    margin-top: 20px;
    font-size: 14px;
    font-weight: bold;
    width: 100%;
    background-color: transparent;
    border: none;
    color: ${({ theme }) => theme.palette.eclipse};
  }
`;
export const StyledCloseButton = styled(Button)`
  && {
    height: 40px;
    margin-top: 20px;
    font-size: 14px;
    font-weight: bold;
    width: 100%;
    background-color: transparent;
    color: ${({ theme }) => theme.palette.eclipse};
    border-color: ${({ theme }) => theme.palette.darkGrey};
    @media ${({ theme }) => `(max-width: ${theme.breakPoint.PHONE})`} {
      width: calc(100% - 32px);
      position: fixed;
      bottom: 16px;
      right: 16px;
      left: 16px;
    }
  }
`;
export const StyledApplyButton = styled(Button)`
  && {
    height: 40px;
    margin-top: 20px;
    font-size: 14px;
    font-weight: bold;
    width: 100%;
  }
`;
export const ButtonDisplayWrapper = styled.div`
  width: 470px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));

  gap: 10px;
  @media ${({ theme }) => `(max-width: ${theme.breakPoint.PHONE})`} {
    width: calc(100% - 32px);
    position: fixed;
    bottom: 16px;
    right: 16px;
    left: 16px;
  }
`;
export const SectionHeaderRoomInfo = styled.div<{
  marginTop?: SizeUnit;
  marginBottom?: SizeUnit;
  overrideDesktopMargin?: SizeUnit;
}>`
  display: flex;
  align-items: flex-end;
  margin-bottom: 10px;
  margin-top: ${({ marginTop }) => marginTop};
  @media (max-width: ${(p) => p.theme.breakPoint.PHONE}) {
    margin-bottom: ${({ marginBottom }) => marginBottom ?? '-12px'};
    align-items: center;
    & > span:first-child {
      align-self: flex-start;
    }
  }
  max-width: 550px;
  width: 100%;
`;
export const SectionTitleTour = styled.h3<{ variant?: 'payment' }>`
  color: ${(p) => p.theme.palette.darkGrey};
  letter-spacing: 1px;
  font-weight: bold;
  vertical-align: bottom;
  margin-left: 3px;
  &[data-role='mail-info-text'] {
    font-size: 20px;
    @media (max-width: ${(p) => p.theme.breakPoint.PHONE}) {
      font-size: 16px;
    }
  }
  ${(p) =>
    p.variant === 'payment' &&
    css`
      font-size: 24px;
      margin: 0 0 0 15px;
      line-height: 1;
      @media (max-width: ${(p) => p.theme.breakPoint.PHONE}) {
        font-size: 18px;
      }
    `}
  @media ${({ theme }) => `(max-width: ${theme.breakPoint.LARGE_1X})`} {
    width: 160px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
`;
export const ReadMoreSection = styled.div`
  margin-top: -15px !important;
  & span {
    justify-content: flex-start;
  }
`;
export const Underline = styled.span`
  text-decoration: underline;
`;
export const TourAbroadContainer = styled.div`
  padding-top: 20px;
`;
export const Title = styled.span`
  font-size: 18px;
  font-weight: 700;
`;
export const Message = styled.span`
  color: ${({ theme }) => theme.palette.BR500};
  font-size: 14px;
  margin-top: 10px;
`;
